<script setup lang="ts">
import { computed, inject } from "vue";
import { useIsInPdfMode } from "~/stores/pdfModeStore";
import {
  FormResponseState,
  formResponseInjectKey,
} from "~/NewFormBuilder/composables/useFormResponse";

import {
  CheckboxOptionsDisplayFormat,
  CheckboxesFormElement,
  FormElementType,
} from "~/api";
import { DefaultFormComponentProps } from "./FormComponentRenderer.vue";
const props = defineProps<DefaultFormComponentProps<CheckboxesFormElement>>();

const { useAnswer } = inject(
  formResponseInjectKey,
  // Cast the type since we know it is always provided. For more info see:
  // https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject
) as FormResponseState;
const answer = useAnswer<FormElementType.CHECKBOXES>(props.modelValue);
const elementProperties = computed(() => props.modelValue.properties);

const options = computed(() => {
  // return the checkbox options from the element properties
  // if inPDF mode, only return the options that are selected
  if (inPdf.value) {
    return (
      elementProperties.value.checkboxOptions?.filter((option) =>
        answer.value?.includes(option.value),
      ) || []
    );
  }
  return elementProperties.value.checkboxOptions || [];
});

const inPdf = useIsInPdfMode();
</script>

<template id="form-checklist-component">
  <div class="ChecklistComponent">
    <slot name="label" :element="modelValue" />

    <el-checkbox-group
      v-model="answer"
      :disabled="isReadOnly"
      :class="{ 'checkbox-hide-header': elementProperties.hideHeader }"
    >
      <el-checkbox
        v-for="option in options"
        :key="option.value"
        :class="{
          'format-compact':
            elementProperties.checkboxOptionsDisplayFormat ===
            CheckboxOptionsDisplayFormat.COMPACT,
        }"
        :label="option.label"
        :value="option.value"
      >
        <span>{{ option.label }}</span>
        <span
          v-if="props.showOptionValues && !inPdf"
          class="component-question-code"
        >
          {{ option.value }}</span
        >
      </el-checkbox>
    </el-checkbox-group>
  </div>
</template>

<style lang="less">
.ChecklistComponent {
  display: flex;
  flex-direction: column;

  .el-checkbox-group {
    margin-top: 8px;

    .el-checkbox {
      display: flex;

      .component-question-code {
        margin-top: -25px;
      }
    }
  }

  label.el-checkbox.format-compact {
    justify-content: flex-start;
    flex-direction: row;
    padding-left: 2em;
  }

  label.el-checkbox {
    flex-direction: row-reverse;
    justify-content: space-between;
    margin: 0px 8px 4px 0;
    padding-right: 0.5em;
  }

  .checkbox {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &.checkbox-hide-header {
      margin-left: 0px;
    }

    .checkbox-label {
      font-size: 14px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  label:nth-child(odd) {
    background: #f7f7f7;
  }

  .el-checkbox__label {
    font-size: var(--el-font-size-base);
    min-height: var(--el-checkbox-height, 32px);
    line-height: normal;
    white-space: normal;
    display: inline-flex;
    align-items: center;
    user-select: text;
  }
}

body.forPdf .ChecklistComponent {
  .el-checkbox__label {
    color: black !important;
  }
}
</style>
